import { icon_microphone_w } from 'components/base/images';
import { T } from 'components/common/text';
import CustomText from 'components/common/text/CustomText';
import React, { useRef } from 'react';
import styled from 'styled-components';
import cx from 'classnames';
import WhiteButton from 'components/common/button/WhiteButton';
import { useCreateProjectMemo } from 'lib/hooks/project/useCreateProjectMemo';
import ProjectInfoHistoryContainer from 'containers/project/ProjectInfoHistoryContainer';
import CustomEditor from 'components/common/input/CustomEditor';
import DotSpinner from 'components/common/spinner/DotSpinner';
import { useShallowAppSelector } from 'store/hooks';
import { TVoiceMemo } from 'lib/types/clinic/projectTypes';
import moment from 'moment';

const ProjectVoiceMemo = () => {
  // SECTION: declare server state
  const { language, loadedSource } = useShallowAppSelector(state => ({
    language: state.base.language,
    loadedSource: state.app.loadedSource,
  }));

  const listeningRef = useRef<HTMLDivElement>(null);
  const {
    historyOpen,
    isRecording,
    isTranscriptionLoading,
    recordingTime,
    senderMemo,
    voiceMemoList,
    isMemoEdit,
    isMemoInit,
    isCreatedOnlineProject,
    onMemoChange,
    onMicrophoneClick,
    onEditMemo,
    onSaveMemo,
    onCancelMemo,
    onClickHistory,
  } = useCreateProjectMemo();

  return (
    <StyledProjectVoiceMemo>
      <ProjectInfoHistoryContainer isOpen={historyOpen} onClick={onClickHistory} isClinic />
      <div className="projectVoiceMemo__container memo">
        <CustomText fontSize={18} fontWeight={400} fontColor="#222">
          <T>PROJECT_MEMO</T>
        </CustomText>
        <div className="projectVoiceMemo__content_box">
          {isMemoInit && (
            <CustomEditor
              value={senderMemo}
              className="memo_input_field"
              // disabled={!isMemoEdit}
              onChange={onMemoChange}
              maxLength={500}
            >
              {isRecording && (
                <div ref={listeningRef} className="record_listening_box">
                  <div
                    style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#D85066' }}
                  />
                  <div className="listening_content">
                    <CustomText fontSize={14} fontColor="#B2B6B8">
                      <T>RECORD_LISTENING</T>
                    </CustomText>
                    <DotSpinner color="#B2B6B8" size={4} gap={2} />
                  </div>
                </div>
              )}
            </CustomEditor>
          )}
          <div className="memo_button_box">
            {isMemoEdit && (
              <CustomText
                fontSize={14}
                fontColor="#B2B6B8"
                fontWeight={400}
              >{`${senderMemo.length} / 500`}</CustomText>
            )}
            <button
              className={cx('record_box', { recording: isRecording || isTranscriptionLoading })}
              onClick={() => onMicrophoneClick(isRecording ? 'stop' : 'start')}
              disabled={loadedSource === 'local'}
            >
              {isRecording || isTranscriptionLoading ? (
                <>
                  {!isTranscriptionLoading && (
                    <CustomText fontSize={16} fontColor="#fff" fontWeight={600}>
                      {recordingTime}
                    </CustomText>
                  )}
                  <img src={icon_microphone_w} alt="icon_microphone_w" />
                  <CustomText fontSize={16} fontColor="#fff" fontWeight={600}>
                    {!!isTranscriptionLoading ? `Saving...` : `Save`}
                  </CustomText>
                </>
              ) : (
                <img src={icon_microphone_w} alt="icon_microphone_w" />
              )}
            </button>
          </div>
        </div>
        <div className="projectVoiceMemo__content_button">
          {isMemoEdit && (
            <WhiteButton
              width={100}
              height={34}
              borderRadius={50}
              onClick={onCancelMemo}
              disabled={isRecording}
            >
              <CustomText fontSize={14} fontWeight={400}>
                <T>GLOBAL_CANCEL</T>
              </CustomText>
            </WhiteButton>
          )}
          {isMemoEdit && (
            <WhiteButton
              width={100}
              height={34}
              borderRadius={50}
              onClick={onSaveMemo}
              disabled={isRecording}
            >
              <CustomText fontSize={14} fontWeight={400}>
                <T>GLOBAL_SAVE</T>
              </CustomText>
            </WhiteButton>
          )}
          {/* {!isMemoEdit && (
            <WhiteButton
              width={100}
              height={34}
              borderRadius={50}
              onClick={onEditMemo}
              disabled={isRecording || isCreatedOnlineProject}
            >
              <CustomText fontSize={14} fontWeight={400}>
                <T>GLOBAL_EDIT</T>
              </CustomText>
            </WhiteButton>
          )} */}
        </div>
      </div>
      <div className="projectVoiceMemo__container history">
        <CustomText fontSize={18} fontWeight={400} fontColor="#222">
          <T>PROJECT_VOICE_HISTORY</T>
        </CustomText>
        <div className="projectVoiceMemo__content_box">
          <div className="memo_input_field recorded">
            {voiceMemoList?.map((item: TVoiceMemo, index: number) => {
              return (
                <div className="memo_recorded_item" key={index}>
                  <div>
                    <CustomText fontSize={16} fontWeight={400} fontColor="#222">
                      {!item.recordDate
                        ? '-'
                        : language === 'en'
                        ? moment
                            .unix(item.recordDate)
                            .locale(language)
                            .format('ddd MMM.DD.YYYY HH:mm')
                        : moment
                            .unix(item.recordDate)
                            .locale(language)
                            .format('YYYY.MM.DD dddd HH:mm')}
                    </CustomText>
                  </div>
                  <div>
                    <CustomText fontSize={16} fontWeight={400} fontColor="#222">
                      {!!item.voiceMemo && item.voiceMemo}
                    </CustomText>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </StyledProjectVoiceMemo>
  );
};

const StyledProjectVoiceMemo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  .projectVoiceMemo__container {
    display: flex;
    flex-direction: column;
    row-gap: 10px;
    .projectVoiceMemo__content_box {
      height: 230px;
      border-radius: 2px;
      border: 1px solid #e1e1e7;
      display: flex;
      flex-direction: column;
      .memo_input_field {
        height: 100%;
        padding: 12px;
        border: none;
        resize: none;
        font-size: 16px;
        color: #444;
        font-weight: 400;
        line-height: 1.5;
        white-space: pre-wrap; /* 엔터나 띄어쓰기를 유지하기 위함 */
        word-wrap: break-word; /* 줄바꿈 자동처리 */
        overflow: auto;
        &:disabled {
          background-color: transparent;
        }
      }
      .recorded {
        display: flex;
        flex-direction: column;
        row-gap: 20px;
      }
      .record_listening_box {
        display: flex;
        width: 100%;
        height: 34px;
        background-color: #f9f9fb;
        margin-top: 12px;
        align-items: center;
        border-radius: 8px;
        padding: 12px;
        column-gap: 8px;
        .listening_content {
          display: flex;
          column-gap: 5px;
        }
      }
      .memo_button_box {
        display: flex;
        padding: 12px;
        position: relative;
        justify-content: flex-end;
        align-items: flex-end;
        column-gap: 16px;
        .record_box {
          background: #444;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 50px;
          height: 50px;
          border-radius: 50px;
          &.recording {
            width: 174px;
            background-color: #d85066;
            border-radius: 50px;
            height: 50px;
            column-gap: 20px;
          }
          &:disabled {
            background-color: #ced2df;
          }
        }
      }
    }
    .projectVoiceMemo__content_button {
      display: flex;
      justify-content: flex-end;
      column-gap: 10px;
      width: 100%;
      height: 34px;
    }
  }
`;

export default ProjectVoiceMemo;
